import {
  ASSIGN_CHAMPIONSHIP_TEMPLATE_GROUP_MUTATION,
  CHAMPIONSHIP_MEDIA_SETTINGS_QUERY,
  ChampionshipMediaKindGql,
  ChampionshipMediaRuleGql,
  ChampionshipMediaSettings,
  ChampionshipMediaSettingsGql,
  UNASSIGN_CHAMPIONSHIP_TEMPLATE_GROUP_MUTATION,
  UPDATE_TOURNAMENT_TIMEZONE_MUTATION,
} from 'modules/championship/domain/graphql/championship-media';
import { MediaKind, isKnownMediaKind } from 'modules/manager/domain/media-kind';
import { baseApi } from 'shared/api/base-api';

export const CHAMPIONSHIP_MEDIA_SETTINGS_TAG = 'ChampionshipMediaSettings' as const;

function mapBlockedReason(value: string | null | undefined): ChampionshipMediaSettings['kinds'][number]['blockedReason'] {
  if (value === 'no_group' || value === 'unsupported' || value === 'no_template' || value === 'no_plan_feature') {
    return value;
  }
  return null;
}

function mapSettings(raw: ChampionshipMediaSettingsGql): ChampionshipMediaSettings {
  const rulesByKind = new Map<string, ChampionshipMediaRuleGql>();
  raw.rules.forEach((rule) => rulesByKind.set(rule.kind, rule));

  const kinds = raw.kinds
    .filter((kind): kind is ChampionshipMediaKindGql & { kind: MediaKind } => isKnownMediaKind(kind.kind))
    .map((kind) => {
      const rule = rulesByKind.get(kind.kind);
      return {
        kind: kind.kind,
        title: kind.title,
        templateType: kind.templateType,
        subjectType: kind.subjectType,
        supported: kind.supported,
        enabled: rule?.enabled ?? false,
        templateName: rule?.template?.name ?? null,
        blockedReason: mapBlockedReason(rule?.blockedReason),
      };
    });

  return {
    championshipId: raw.championshipId,
    timezone: raw.timezone,
    templateGroup: raw.templateGroup,
    kinds,
  };
}

export const championshipMediaApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    championshipMediaSettings: build.query<ChampionshipMediaSettings, number>({
      query: (championshipId) => ({
        query: CHAMPIONSHIP_MEDIA_SETTINGS_QUERY,
        variables: { championshipId },
      }),
      transformResponse: (raw: { championshipMediaSettings: ChampionshipMediaSettingsGql }) =>
        mapSettings(raw.championshipMediaSettings),
      providesTags: (_result, _error, championshipId) => [{ type: CHAMPIONSHIP_MEDIA_SETTINGS_TAG, id: championshipId }],
    }),
    assignChampionshipTemplateGroup: build.mutation<ChampionshipMediaSettings, { championshipId: number; templateGroupId: number }>(
      {
        query: ({ championshipId, templateGroupId }) => ({
          query: ASSIGN_CHAMPIONSHIP_TEMPLATE_GROUP_MUTATION,
          variables: { championshipId, templateGroupId },
        }),
        transformResponse: (raw: { assignChampionshipTemplateGroup: ChampionshipMediaSettingsGql }) =>
          mapSettings(raw.assignChampionshipTemplateGroup),
        invalidatesTags: (_result, _error, { championshipId }) => [{ type: CHAMPIONSHIP_MEDIA_SETTINGS_TAG, id: championshipId }],
      },
    ),
    unassignChampionshipTemplateGroup: build.mutation<ChampionshipMediaSettings, number>({
      query: (championshipId) => ({
        query: UNASSIGN_CHAMPIONSHIP_TEMPLATE_GROUP_MUTATION,
        variables: { championshipId },
      }),
      transformResponse: (raw: { unassignChampionshipTemplateGroup: ChampionshipMediaSettingsGql }) =>
        mapSettings(raw.unassignChampionshipTemplateGroup),
      invalidatesTags: (_result, _error, championshipId) => [{ type: CHAMPIONSHIP_MEDIA_SETTINGS_TAG, id: championshipId }],
    }),
    updateTournamentTimezone: build.mutation<{ id: number; timezone: string }, { tournamentId: number; timezone: string }>({
      query: ({ tournamentId, timezone }) => ({
        query: UPDATE_TOURNAMENT_TIMEZONE_MUTATION,
        variables: { tournamentId, timezone },
      }),
      transformResponse: (raw: { updateTournamentTimezone: { id: number; timezone: string } }) => raw.updateTournamentTimezone,
    }),
  }),
  overrideExisting: false,
});

export const {
  useChampionshipMediaSettingsQuery,
  useAssignChampionshipTemplateGroupMutation,
  useUnassignChampionshipTemplateGroupMutation,
  useUpdateTournamentTimezoneMutation,
} = championshipMediaApi;
